"use client";

import { Mail, Sun, Moon, Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { MobileSidebar } from "@/components/layout/mobile-sidebar";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { useMessageStore } from "@/stores/message-store";
import { useUiModeStore } from "@/stores/ui-mode-store";
import { useTranslations } from "@/i18n/useTranslations";
import { useShowTryGuidedButton } from "@/hooks/use-guided-ui-mode";
import { buildBreadcrumbs, stripLocale } from "@/lib/navigation-config";

export function Header() {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslations();
  const tCommon = useTranslations("common");
  const unreadCount = useMessageStore((s) => s.unreadCount);
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations("guided");

  // Get current page label for mobile display
  const breadcrumbs = buildBreadcrumbs(stripLocale(location.pathname));
  const currentPageLabel = breadcrumbs.length > 0
    ? (breadcrumbs[breadcrumbs.length - 1].labelKey
        ? t(breadcrumbs[breadcrumbs.length - 1].labelKey)
        : null)
    : null;

  return (
    <header className="sticky top-0 z-30 h-16 bg-overlay/70 backdrop-blur-xl border-b border-surface">
      <div className="flex h-full items-center justify-between px-4 md:px-6">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <div className="md:hidden">
            <MobileSidebar />
          </div>

          {/* Mobile page title */}
          {currentPageLabel && (
            <span className="sm:hidden font-medium text-foreground truncate">
              {currentPageLabel}
            </span>
          )}

          {/* Breadcrumb */}
          <Breadcrumb className="hidden sm:flex" />
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Switch back to Guided UI (only for guided-eligible roles in classic mode) */}
          {guidedEligible && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUiMode("guided")}
              aria-label={tGuided("switchGuided")}
              title={tGuided("switchGuided")}
            >
              <Sparkles className="h-4 w-4" />
              <span className="ml-1 hidden lg:inline">{tGuided("switchGuided")}</span>
            </Button>
          )}

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={tCommon("toggleTheme")}
          >
            <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          {/* Messages */}
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => navigate("/dashboard/messages")}
            aria-label={tCommon("openMessages")}
          >
            <Mail className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-primary-foreground text-[10px] font-bold px-1">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}
